import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle, X, Square } from 'lucide-react'
import { extractionApi, type ExtractedRow } from '@/api/extraction'
import { fieldsApi } from '@/api/fields'
import { useProcessingJob } from '@/hooks/useProcessingJob'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'

function confColor(c: number | null): string {
  if (c === null) return 'text-gray-500 bg-gray-50'
  if (c >= 0.8) return 'text-green-600 bg-green-50'
  if (c >= 0.5) return 'text-yellow-600 bg-yellow-50'
  return 'text-red-600 bg-red-50'
}

interface Props {
  projectId: string
  jobId: string
  onClose: () => void
}

/**
 * A status-only modal for viewing an already-running job's progress.
 * Used when clicking the "Extracting X/Y" indicator in the project header.
 */
export function ProcessingStatusModal({ projectId, jobId, onClose }: Props) {
  const queryClient = useQueryClient()
  const [docResults, setDocResults] = useState<Record<string, { docName: string; fields: Record<string, { value: string | null; confidence: number | null }>; avgConf: number | null }>>({})

  const stopJobMutation = useMutation({
    mutationFn: () => extractionApi.stopJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['extraction-jobs', projectId] })
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
      queryClient.invalidateQueries({ queryKey: ['results', projectId] })
    },
  })

  const { data: fields = [] } = useQuery({
    queryKey: ['fields', projectId],
    queryFn: () => fieldsApi.list(projectId),
  })

  const { events, isComplete, progress } = useProcessingJob(jobId)
  const progressPct = progress ? Math.round((progress.done / progress.total) * 100) : 0

  // Build per-doc results from events.
  // extractionApi.getResults returns the compact paginated response shape:
  // { documents: [{ document_id, document_name, rows: [{ fields: {[name]: {value, confidence}} }] }] }
  // The table renders cells by field id, so we map display names back to ids.
  useEffect(() => {
    const processed = events.filter((e) => e.event === 'document_processed' && e.document_id)
    if (processed.length === 0) return
    const lastEvt = processed[processed.length - 1]
    if (!lastEvt.document_id) return

    const nameToId = new Map<string, string>()
    for (const f of fields) {
      nameToId.set(f.display_name || f.name, f.id)
    }

    extractionApi
      .getResults(projectId, lastEvt.document_id, jobId)
      .then((res) => {
        const doc = Array.isArray(res?.documents) ? res.documents[0] : null
        if (!doc || !Array.isArray(doc.rows)) return

        const fv: Record<string, { value: string | null; confidence: number | null }> = {}
        let tc = 0
        let cc = 0
        for (const row of doc.rows) {
          if (!row || !row.fields) continue
          for (const [fieldName, val] of Object.entries(row.fields)) {
            if (!val) continue
            const fid = nameToId.get(fieldName)
            if (!fid) continue
            fv[fid] = { value: val.value ?? null, confidence: val.confidence ?? null }
            if (val.confidence !== null && val.confidence !== undefined) {
              tc += val.confidence
              cc++
            }
          }
        }

        setDocResults((prev) => ({
          ...prev,
          [lastEvt.document_id!]: {
            docName: lastEvt.document_name || lastEvt.document_id!,
            fields: fv,
            avgConf: cc > 0 ? tc / cc : null,
          },
        }))
      })
      .catch(() => {})
  }, [events.length, jobId, projectId, fields])

  useEffect(() => {
    if (isComplete) {
      queryClient.invalidateQueries({ queryKey: ['results', projectId] })
      queryClient.invalidateQueries({ queryKey: ['extraction-jobs', projectId] })
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
    }
  }, [isComplete, projectId, queryClient])

  const fieldIds = fields.map((f) => f.id)
  const fieldNames = fields.map((f) => f.display_name || f.name)
  const docRows = Object.entries(docResults)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-3xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Processing Documents</h2>
          <div className="flex items-center gap-1">
            {!isComplete && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  if (confirm('Stop this extraction job? Documents already processed will keep their results.')) {
                    stopJobMutation.mutate()
                  }
                }}
                disabled={stopJobMutation.isPending}
              >
                <Square className="h-3.5 w-3.5 mr-1" />
                {stopJobMutation.isPending ? 'Stopping...' : 'Stop'}
              </Button>
            )}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-muted">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <Progress value={progressPct} className="h-2" />
          <p className="text-sm text-center text-muted-foreground">
            {progress ? `${progress.done} / ${progress.total} documents` : 'Loading...'}
          </p>

          {docRows.length > 0 && (
            <div className="max-h-56 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium whitespace-nowrap">File</th>
                    {fieldNames.map((name, i) => (
                      <th key={fieldIds[i]} className="text-left p-2 font-medium whitespace-nowrap">{name}</th>
                    ))}
                    <th className="text-center p-2 font-medium whitespace-nowrap">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {docRows.map(([docId, doc]) => (
                    <tr key={docId} className="border-t">
                      <td className="p-2 font-medium truncate max-w-[160px] whitespace-nowrap" title={doc.docName}>
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                          {doc.docName}
                        </div>
                      </td>
                      {fieldIds.map((fid) => {
                        const cell = doc.fields[fid]
                        return (
                          <td key={fid} className="p-2 max-w-[150px] truncate whitespace-nowrap" title={cell?.value || ''}>
                            {cell?.value || <em className="text-gray-400">null</em>}
                          </td>
                        )
                      })}
                      <td className="p-2 text-center">
                        {doc.avgConf !== null ? (
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${confColor(doc.avgConf)}`}>
                            {(doc.avgConf * 100).toFixed(0)}%
                          </Badge>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isComplete && progress && progress.done < progress.total && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Processing {progress.total - progress.done} remaining...
            </div>
          )}

          {isComplete && (
            <div className="flex items-center gap-2 justify-center text-sm text-green-600 font-medium pt-1">
              <CheckCircle className="h-5 w-5" />
              Extraction complete
            </div>
          )}

          {!isComplete && (
            <p className="text-[11px] text-center text-muted-foreground">
              You can close this dialog — processing continues in the background
            </p>
          )}

          {isComplete && (
            <div className="flex gap-2 pt-2">
              <Button onClick={onClose} className="w-full">Done</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
